import type { ActionConfig } from "common/contentActions";
import type { QueryAdapterOptions } from "common/graphqlAdapter";

import type { Variant as CardVariant } from "tds/card";
import type { Layout } from "tds/contentCollectionCard";
import type { Config as LoadingStateConfig } from "tds/contentCollectionLoadingState";
import type { ContentSummaryConfig, ContentSummary } from "tds/contentSummary";

import { fromCommunity, fromLearning } from "common/contentSummary";
import type { LearningType } from "common/learning";

export type CommunityNodeSummaryType = {
  id: string;
  type: string;
  name: string;
  typeLocalized: string;
  avatar: string;
  description: string;
  url: string;
};

export type CommunityNodeSummariesType = CommunityNodeSummaryType[];

export type CollectionChangeEvent = CustomEvent<{
  state: LoadingStateConfig["state"];
  items?: ContentSummary[];
}>;

export type CollectionConfig = {
  card?: {
    ctaAlways?: boolean;
    ctaDisabled?: boolean;
    ctaHref?: string;
    ctaLabel?: string;
    ctaSpinner?: boolean;
    ctaTarget?: string;
    description?: string;
    layout?: Layout;
    title?: string;
    variant?: CardVariant;
  };
  item?: ContentSummaryConfig;
  limit?: number;
  state?: Omit<LoadingStateConfig, "state">;
};

export type CollectionItem = {
  action?: ActionConfig;
  summary: ContentSummary;
};

export interface CollectionState {
  endCursor?: string | null;
  startCursor?: string | null;
}

export interface CollectionQuery<
  T,
  S extends CollectionState = CollectionState
> {
  getCollectionItems(result: T): CollectionItem[];
  getQueryOptions?(state?: S): QueryAdapterOptions;
  getTotalItemCount?(result?: T): number | undefined;
  getState?(result: T): S | undefined;
  hasMore?(result: T): boolean | undefined;
  /**
   * @deprecated use getQueryOptions()
   */
  queryOptions?: QueryAdapterOptions;
  useFetch?: boolean;
}

export const DEFAULT_LIMIT = 3;

export function createConfig(
  config: CollectionConfig | null,
  defaultConfig?: CollectionConfig | null
): CollectionConfig {
  return {
    ...defaultConfig,
    ...config,
    card: {
      layout: "stack",
      variant: "default",
      ...defaultConfig?.card,
      ...config?.card,
    },
    item: {
      variant: "brick",
      ...defaultConfig?.item,
      ...config?.item,
    },
    limit: config?.limit ?? defaultConfig?.limit ?? DEFAULT_LIMIT,
    state: {
      ...defaultConfig?.state,
      ...config?.state,
      loading: {
        stencilCount: config?.limit ?? defaultConfig?.limit ?? DEFAULT_LIMIT,
        ...defaultConfig?.state?.loading,
        ...config?.state?.loading,
        stencilVariant:
          config?.state?.loading?.stencilVariant ??
          config?.item?.variant ??
          "brick",
      },
    },
  };
}

export function getCollectionItem(
  learning: LearningType,
  user?: { username: string },
  eventData?: { name: string; id: string }
): CollectionItem {
  return {
    action: { learning, user, eventData },
    summary: fromLearning(learning),
  };
}

export function getCommunityCollectionItem(
  communityItem: CommunityNodeSummaryType
): CollectionItem {
  return {
    summary: fromCommunity(communityItem),
  };
}
